// =====================================
// src/pages/QuoteAction.jsx (sin botón "Ir al sistema")
// Página de Aprobación / Solicitud de cambios para CLIENTES
// =====================================
import React, { useEffect, useState } from 'react';
import {
  Container, Box, Typography, Button, Alert, CircularProgress, Stack,
  TextField, Paper
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import EditNoteIcon from '@mui/icons-material/EditNote';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import { useParams, Link as RouterLink, useSearchParams } from 'react-router-dom';
import { acceptQuote, submitChangeRequest } from '../services/quoteActions';

const BRAND_LOGO_URL = "https://www.inflablesdasilva.com/wp-content/uploads/2026/02/WhatsApp-Image-2026-02-16-at-6.45.52-AM-1.jpeg";

export default function QuoteAction() {
  const { quoteNumber = '', action = '' } = useParams();
  const [search] = useSearchParams();
  const token = search.get('token') || undefined;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [ok, setOk] = useState(false);
  const [aiAnalysis, setAiAnalysis] = useState('');

  const [message, setMessage] = useState('');
  const [contact, setContact] = useState('');

  const isAccept = String(action).toLowerCase() === 'accept';
  const isModify = String(action).toLowerCase() === 'modify';

  useEffect(() => {
    let alive = true;
    if (isAccept && quoteNumber) {
      (async () => {
        try {
          setLoading(true);
          setError('');
          setOk(false);
          const res = await acceptQuote(quoteNumber, token);
          if (!alive) return;
          if (res && (res.ok || res.status === 'ok' || res.success)) {
            setOk(true);
          } else {
            setOk(true);
          }
        } catch (e) {
          const msg = e?.response?.data?.message || e?.message || 'Error aprobando la cotización';
          setError(msg);
        } finally {
          if (alive) setLoading(false);
        }
      })();
    }
    return () => { alive = false; };
  }, [isAccept, quoteNumber, token]);

  const onSubmitChanges = async (ev) => {
    ev.preventDefault();
    if (!message.trim()) {
      setError('Por favor cuéntanos qué necesitas cambiar.');
      return;
    }
    try {
      setLoading(true);
      setError('');
      setOk(false);
      const res = await submitChangeRequest(quoteNumber, message.trim(), token, contact.trim() || undefined);
      setOk(true);
      if (res?.aiAnalysis) setAiAnalysis(res.aiAnalysis);
    } catch (e) {
      const msg = e?.response?.data?.message || e?.message || 'Error enviando la solicitud de cambios';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  if (!isAccept && !isModify) {
    return (
      <Container maxWidth="md" sx={{ py: 6, textAlign: 'center' }}>
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <img src={BRAND_LOGO_URL} alt="Inflables Da Silva" style={{ height: 64, objectFit: 'contain' }} />
        </Box>
        <Typography variant="h5" sx={{ mb: 2 }}>
          Acción no reconocida
        </Typography>
        <Typography sx={{ mb: 3 }}>
          Usa uno de estos enlaces desde el PDF:
        </Typography>
        <Stack direction="row" spacing={2} justifyContent="center">
          <Button component={RouterLink} to={`/quote/${encodeURIComponent(quoteNumber)}/accept${token ? `?token=${encodeURIComponent(token)}` : ''}`} variant="contained" startIcon={<DoneAllIcon/>}>
            Aprobar cotización
          </Button>
          <Button component={RouterLink} to={`/quote/${encodeURIComponent(quoteNumber)}/modify${token ? `?token=${encodeURIComponent(token)}` : ''}`} variant="outlined" startIcon={<EditNoteIcon/>}>
            Solicitar cambios
          </Button>
        </Stack>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 6 }}>
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <img src={BRAND_LOGO_URL} alt="Inflables Da Silva" style={{ height: 64, objectFit: 'contain' }} />
      </Box>

      <Typography variant="h4" align="center" sx={{ fontWeight: 700, mb: 1 }}>
        {isAccept ? 'Aprobación de cotización' : 'Solicitar cambios a la cotización'}
      </Typography>
      <Typography variant="subtitle1" align="center" color="text.secondary" sx={{ mb: 4 }}>
        Cotización: <strong>{quoteNumber}</strong>
      </Typography>

      {loading && (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      )}

      {!loading && error && (
        <Alert severity="error" icon={<ErrorOutlineIcon />} sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {!loading && !error && ok && (
        <>
          <Alert severity="success" icon={<CheckCircleIcon />} sx={{ mb: 2 }}>
            {isAccept ? '¡Cotización aprobada correctamente!' : '¡Solicitud enviada correctamente!'}
          </Alert>
          {isModify && aiAnalysis && (
            <Paper elevation={1} sx={{ p: 2, mb: 3, bgcolor: '#f0fdf4', border: '1px solid #86efac' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5, color: '#15803d' }}>
                Respuesta automática de nuestro sistema:
              </Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-line', color: '#166534' }}>
                {aiAnalysis}
              </Typography>
            </Paper>
          )}
        </>
      )}

      {/* Vista de aceptación: dejamos SOLO el acceso a solicitar cambios */}
      {isAccept && !loading && (
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center" sx={{ mt: 2 }}>
          <Button
            component={RouterLink}
            to={`/quote/${encodeURIComponent(quoteNumber)}/modify${token ? `?token=${encodeURIComponent(token)}` : ''}`}
            variant="outlined"
            size="large"
            startIcon={<EditNoteIcon />}
          >
            Solicitar cambios
          </Button>
        </Stack>
      )}

      {/* Vista de solicitud de cambios: eliminamos "Ir al sistema" */}
      {isModify && (
        <Paper elevation={2} sx={{ p: 3 }}>
          <Box component="form" onSubmit={onSubmitChanges}>
            <Stack spacing={2}>
              <TextField
                label="Describa los cambios que necesita"
                placeholder="Ej: Cambiar producto, ajustar cantidad, otro horario, etc."
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                fullWidth
                multiline
                minRows={4}
                required
              />
              <TextField
                label="Datos de contacto (opcional)"
                placeholder="Teléfono o email para responderle"
                value={contact}
                onChange={(e) => setContact(e.target.value)}
                fullWidth
              />
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  disabled={loading}
                  startIcon={<EditNoteIcon />}
                >
                  Enviar solicitud
                </Button>
              </Stack>
            </Stack>
          </Box>
        </Paper>
      )}
    </Container>
  );
}
